import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useInterviewSettingsForm } from '@/features/campaign-interview-settings/use-interview-settings-form'
import { useEntitlements } from '@/lib'
import {
	type CampaignInterviewSettingsUpdateRequest,
	stripeFeature,
	useCampaignPipelineConfigShow,
} from '@jobilla/nexus-sdk'
import { Link } from '@tanstack/react-router'
import { createFileRoute } from '@tanstack/react-router'
import { CheckIcon } from 'lucide-react'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_auth/company/$companyId/$campaignId/settings/interview')({
	component: RouteComponent,
})

function RouteComponent() {
	const { campaignId } = Route.useParams()

	const { t } = useTranslation('common')
	const {
		form,
		isLoading,
		isLoadError,
		isDirty,
		savedRecently,
		isPending,
		validationError,
		hasGenericError,
		updateForm,
		handleSave,
	} = useInterviewSettingsForm(campaignId)

	const pipelineQuery = useCampaignPipelineConfigShow(campaignId)
	const pipelineStages = pipelineQuery.data?.stages ?? []

	const { can, isLoading: entitlementsLoading } = useEntitlements()
	const jobillaMeetEnabled = !entitlementsLoading && can(stripeFeature['jobilla-meet'])

	const [attemptedSave, setAttemptedSave] = useState(false)

	// An entitlement-gated type that's already persisted stays selectable so the dropdown
	// never renders blank; hard-hidden types (Zoom) are excluded even when
	// persisted, since they're unusable until their integration ships.
	const locationTypeOptions = LOCATION_TYPE_OPTIONS.filter((option) => {
		if (HIDDEN_LOCATION_TYPES.has(option.value)) {
			return false
		}
		if (option.value === form.interviewMeetingLocationType) {
			return true
		}
		if (CALENDAR_LOCATION_TYPES.has(option.value)) {
			return true
		}
		if (option.value === 'jobilla_meet') {
			return jobillaMeetEnabled
		}
		return true
	})

	if (isLoading) {
		return (
			<div className="flex items-center justify-center min-h-64 text-sm text-grey-400">
				{t('campaign.settings.interviewScheduling.loading')}
			</div>
		)
	}

	if (isLoadError) {
		return (
			<div className="flex items-center justify-center min-h-64 text-sm text-red-500">
				{t('campaign.settings.interviewScheduling.loadError')}
			</div>
		)
	}

	function setField<K extends keyof CampaignInterviewSettingsUpdateRequest>(
		key: K,
		value: CampaignInterviewSettingsUpdateRequest[K],
	): void {
		updateForm((prev) => ({
			...prev,
			[key]: value,
		}))
	}

	const locationTypeRequiresDetails = form.interviewMeetingLocationType != null
		&& LOCATION_TYPES_REQUIRING_DETAILS.has(form.interviewMeetingLocationType)

	const fieldErrors = getFieldErrors(form)
	const hasFieldErrors = fieldErrors.length > 0

	function onSave(): void {
		setAttemptedSave(true)
		if (getFieldErrors(form).length > 0) {
			return
		}
		handleSave()
	}

	return (
		<div className="max-w-2xl space-y-8">
			<div>
				<h2 className="text-lg font-semibold text-grey-900 mb-1">
					{t('campaign.settings.interviewScheduling.title')}
				</h2>
				<p className="text-sm text-grey-500">
					<Trans
						i18nKey="campaign.settings.interviewScheduling.subtitle"
						ns="common"
						components={{
							availabilityLink: (
								<Link
									to="/account/availability"
									className="underline hover:text-grey-700"
								/>
							),
						}}
					/>
				</p>
			</div>

			<section className="space-y-6">
				{/* Duration */}
				<div className="space-y-2">
					<label className="block text-sm font-medium text-grey-700">
						{t('campaign.settings.interviewScheduling.duration.label')}{' '}
						<span className="text-red-500">*</span>
					</label>
					<div className="flex gap-2">
						{DURATION_OPTIONS.map((minutes) => (
							<button
								key={minutes}
								type="button"
								onClick={() => setField('interviewDurationMinutes', minutes)}
								className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
									form.interviewDurationMinutes === minutes
										? 'bg-brand-600 text-white border-brand-600'
										: 'bg-white text-grey-700 border-grey-300 hover:border-brand-400'
								}`}
							>
								{t('campaign.settings.interviewScheduling.duration.option', { minutes })}
							</button>
						))}
						{form.interviewDurationMinutes != null && (
							<button
								type="button"
								onClick={() => setField('interviewDurationMinutes', null)}
								className="px-3 py-2 rounded-lg text-sm text-grey-400 hover:text-grey-600 transition-colors"
							>
								{t('campaign.settings.interviewScheduling.clear')}
							</button>
						)}
					</div>
					{attemptedSave && fieldErrors.includes('duration')
						? (
							<p className="text-xs text-red-500">
								{t('campaign.settings.interviewScheduling.errors.durationRequired')}
							</p>
						)
						: null}
				</div>

				{/* Meeting location */}
				<div className="space-y-2">
					<label className="block text-sm font-medium text-grey-700">
						{t('campaign.settings.interviewScheduling.meetingLocation.label')}{' '}
						<span className="text-red-500">*</span>
					</label>
					<Select
						value={form.interviewMeetingLocationType ?? ''}
						onValueChange={(value) => {
							const locationType = (value || null) as CampaignInterviewSettingsUpdateRequest[
								'interviewMeetingLocationType'
							]
							setField('interviewMeetingLocationType', locationType)
							if (locationType == null || !LOCATION_TYPES_REQUIRING_DETAILS.has(locationType)) {
								setField('interviewMeetingLocationDetails', null)
							}
						}}
					>
						<SelectTrigger className="w-full bg-white">
							<SelectValue
								placeholder={t('campaign.settings.interviewScheduling.meetingLocation.placeholder')}
							/>
						</SelectTrigger>
						<SelectContent>
							{locationTypeOptions.map((option) => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					{locationTypeRequiresDetails
						? (
							<Input
								type="text"
								value={form.interviewMeetingLocationDetails ?? ''}
								onChange={(e) => setField('interviewMeetingLocationDetails', e.target.value || null)}
								placeholder={t(
									'campaign.settings.interviewScheduling.meetingLocation.detailsPlaceholder',
								)}
								className="bg-white"
							/>
						)
						: null}
					{attemptedSave && fieldErrors.includes('location')
						? (
							<p className="text-xs text-red-500">
								{t('campaign.settings.interviewScheduling.errors.locationRequired')}
							</p>
						)
						: null}
					{attemptedSave && fieldErrors.includes('locationDetails')
						? (
							<p className="text-xs text-red-500">
								{t('campaign.settings.interviewScheduling.errors.locationDetailsRequired')}
							</p>
						)
						: null}
				</div>

				{/* Booking window */}
				<div className="space-y-2">
					<label className="block text-sm font-medium text-grey-700">
						{t('campaign.settings.interviewScheduling.bookingWindow.label')}{' '}
						<span className="text-red-500">*</span>
					</label>
					<div className="flex items-center gap-2">
						<Input
							type="text"
							inputMode="numeric"
							pattern="\d+"
							value={form.interviewBookingWindowDays ?? ''}
							onChange={(e) =>
								setField(
									'interviewBookingWindowDays',
									e.target.value ? Number(e.target.value) : null,
								)}
							placeholder="14"
							className="w-28 bg-white"
						/>
						<span className="text-sm text-grey-500">
							{t('campaign.settings.interviewScheduling.bookingWindow.unit')}
						</span>
					</div>
					{attemptedSave && fieldErrors.includes('bookingWindow')
						? (
							<p className="text-xs text-red-500">
								{t('campaign.settings.interviewScheduling.errors.bookingWindowRequired')}
							</p>
						)
						: null}
				</div>

				{/* Token expiry */}
				<div className="space-y-2">
					<label className="block text-sm font-medium text-grey-700">
						{t('campaign.settings.interviewScheduling.tokenExpiry.label')}{' '}
						<span className="text-red-500">*</span>
					</label>
					<div className="flex items-center gap-2">
						<Input
							type="text"
							inputMode="numeric"
							pattern="\d+"
							value={form.interviewTokenExpiryDays ?? ''}
							onChange={(e) =>
								setField('interviewTokenExpiryDays', e.target.value ? Number(e.target.value) : null)}
							placeholder="7"
							className="w-28 bg-white"
						/>
						<span className="text-sm text-grey-500">
							{t('campaign.settings.interviewScheduling.tokenExpiry.unit')}
						</span>
					</div>
					{attemptedSave && fieldErrors.includes('tokenExpiry')
						? (
							<p className="text-xs text-red-500">
								{t('campaign.settings.interviewScheduling.errors.tokenExpiryRequired')}
							</p>
						)
						: null}
				</div>

				{/* Description */}
				<div className="space-y-2">
					<label className="block text-sm font-medium text-grey-700">
						{t('campaign.settings.interviewScheduling.description.label')}
					</label>
					<p className="text-xs text-grey-400">
						{t('campaign.settings.interviewScheduling.description.hint')}
					</p>
					<Textarea
						value={form.interviewDescription ?? ''}
						onChange={(e) => setField('interviewDescription', e.target.value || null)}
						rows={3}
						className="resize-none bg-white"
					/>
				</div>

				{/* Interview-booked stage */}
				<div className="space-y-2">
					<label className="block text-sm font-medium text-grey-700">
						{t('campaign.settings.interviewScheduling.bookedStage.label')}
					</label>
					<p className="text-xs text-grey-400">
						{t('campaign.settings.interviewScheduling.bookedStage.hint')}
					</p>
					<Select
						value={form.interviewBookedStageId ?? 'none'}
						onValueChange={(stageId) => {
							const resolvedId = stageId === 'none' ? null : stageId
							const stage = pipelineStages.find((s) => s.id === resolvedId)
							setField('interviewBookedStageId', resolvedId)
							setField('interviewBookedStageType', stage?.type ?? null)
						}}
						disabled={pipelineQuery.isLoading}
					>
						<SelectTrigger className="w-full bg-white">
							<SelectValue
								placeholder={t('campaign.settings.interviewScheduling.bookedStage.placeholder')}
							/>
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="none" className="italic">
								{t('campaign.settings.interviewScheduling.bookedStage.placeholder')}
								<span className="text-gray-600">
									- {t('campaign.settings.interviewScheduling.bookedStage.noneDescription')}
								</span>
							</SelectItem>
							{pipelineStages.map((stage) => (
								<SelectItem key={stage.id} value={stage.id}>
									{stage.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</section>

			{/* Save bar */}
			<div className="flex flex-col items-start gap-4">
				<Button
					type="button"
					onClick={onSave}
					disabled={isPending || !isDirty}
				>
					{isPending
						? t('campaign.settings.interviewScheduling.save.saving')
						: savedRecently
						? (
							<>
								<CheckIcon className="size-4" />
								{t('campaign.settings.interviewScheduling.save.saved')}
							</>
						)
						: t('campaign.settings.interviewScheduling.save.button')}
				</Button>
				{isDirty && !isPending && !savedRecently
					? (
						<span className="text-xs text-grey-400">
							{t('campaign.settings.interviewScheduling.save.unsavedChanges')}
						</span>
					)
					: null}
				{attemptedSave && hasFieldErrors
					? (
						<span className="text-xs text-red-500">
							{t('campaign.settings.interviewScheduling.errors.summary')}
						</span>
					)
					: null}
				{validationError != null && <span className="text-xs text-red-500">{validationError}</span>}
				{hasGenericError
					? (
						<span className="text-xs text-red-500">
							{t('campaign.settings.interviewScheduling.save.errorGeneric')}
						</span>
					)
					: null}
			</div>
		</div>
	)
}

const DURATION_OPTIONS = [20, 30, 45, 60] as const

const LOCATION_TYPE_OPTIONS = [
	{
		value: 'jobilla_meet',
		label: 'Jobilla Meet',
	},
	{
		value: 'google_meet',
		label: 'Google Meet',
	},
	{
		value: 'teams',
		label: 'Microsoft Teams',
	},
	{
		value: 'zoom',
		label: 'Zoom',
	},
	{
		value: 'phone',
		label: 'Phone',
	},
	{
		value: 'in_person',
		label: 'In person',
	},
	{
		value: 'custom',
		label: 'Custom',
	},
] as const

const LOCATION_TYPES_REQUIRING_DETAILS = new Set(['phone', 'in_person', 'custom'])

// Meet/Teams links are only produced via a connected calendar, so they're offered
// only while calendar integration is live. Zoom rides an unbuilt VideoIntegrations
// path, so it's hidden until that lands.
const CALENDAR_LOCATION_TYPES = new Set(['google_meet', 'teams'])
const HIDDEN_LOCATION_TYPES = new Set(['zoom'])

type FieldError = 'duration' | 'location' | 'locationDetails' | 'bookingWindow' | 'tokenExpiry'

// Mirrors the backend's isCampaignDefinitionComplete — an incomplete config isn't bookable.
function getFieldErrors(form: CampaignInterviewSettingsUpdateRequest): FieldError[] {
	const errors: FieldError[] = []
	if (form.interviewDurationMinutes == null) {
		errors.push('duration')
	}
	if (form.interviewMeetingLocationType == null) {
		errors.push('location')
	} else if (
		LOCATION_TYPES_REQUIRING_DETAILS.has(form.interviewMeetingLocationType)
		&& (form.interviewMeetingLocationDetails ?? '').trim() === ''
	) {
		errors.push('locationDetails')
	}
	if (form.interviewBookingWindowDays == null || form.interviewBookingWindowDays < 1) {
		errors.push('bookingWindow')
	}
	if (form.interviewTokenExpiryDays == null || form.interviewTokenExpiryDays < 1) {
		errors.push('tokenExpiry')
	}
	return errors
}
